import React, {Component} from "react"
import Papa from "papaparse"
import {chunk, flatten} from "lodash"
import moment from "moment"

import {Label} from "@irati/ui/src/Typography"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import adminReportingService from "@irati/adminReporting/services/adminReporting"
import authentication from "@irati/auth"
import {Recognition} from "@irati/types"
import Flex from "@irati/ui/src/Flex"
import Button from "@irati/ui/src/Button"
import makeCSV from "@irati/utils/csv/makeCSV"
import {Tooltip} from "@irati/ui/src/Tooltip"
import firstnameLastname from "@irati/identity/helpers/user/firstnameLastname"
import {promiseSerialAll} from "@irati/utils/promise"
import RecognitionService from "@irati/recognition/services/Recognition"

import {ALL_TEAMS} from "../../Region"
import buildMonthAndDurationFields from "../helpers/buildMonthAndDurationFields"

interface Props extends InjectIntl {
  showButtons: boolean
  onDownload?: () => void
  hideText?: boolean
}

interface State {
  load: Promise<void> | null
}

class MostPopularPerks extends Component<Props, State> {
  state = {
    load: null
  }

  downloadReport = () => {
    const {formatMessage, regionId, category, isTeams, onDownload} = this.props

    if (onDownload) {
      onDownload()
    }

    const regionIds = regionId !== ALL_TEAMS ? [regionId] : undefined
    const {month, duration} = buildMonthAndDurationFields(this.props.duration)

    const load = adminReportingService
      .getCelebrationsReport({
        businessId: authentication.business().uuid,
        regionIds,
        duration,
        category,
        month
      })
      .then(({recognition}) => {
        const usersOrTeams = isTeams ? recognition.teams : recognition.users
        const allRecognitions = flatten(
          usersOrTeams.reduce((arr, t) => {
            return [...arr, t.recognitions]
          }, [])
        )
        const uniqueRecognitions = [...new Map(allRecognitions.map((item) => [item["recognitionId"], item])).values()]
        let recognitions: Recognition[] = []

        const chunks = chunk(uniqueRecognitions, 10)
        const funcs = chunks.map((recognitionsChunk) => () => {
          return Promise.all(
            recognitionsChunk.map((recognitionChunk) => {
              const {recognitionId, rpTransferred} = recognitionChunk
              return RecognitionService.getWithTeams(recognitionId)
                .then((r) => {
                  if (r) {
                    recognitions = [...recognitions, {...r, rpTransferred}]
                  }
                })
                .catch(() => {})
            })
          )
        })

        return promiseSerialAll(funcs).then(async () => {
          const recognitionsPerReceiver = recognitions.reduce((recognitionArray, r) => {
            if (r?.receivers?.results?.length || 0 > 1) {
              const recognitionsSplitByReceiver = r.receivers.results.reduce((arr, receiver) => {
                return [...arr, {...r, receiver, rpTransferred: r.rpTransferred}]
              }, [])
              return [...recognitionArray, ...recognitionsSplitByReceiver]
            }
            return [...recognitionArray, r]
          }, [])

          const headers = [
            formatMessage({id: "admin.reporting.celebrations.download.columns.date"}),
            formatMessage({id: "admin.reporting.celebrations.download.columns.author"}),
            formatMessage({id: "admin.reporting.celebrations.download.columns.authorsTeams"}),
            formatMessage({id: "admin.reporting.celebrations.download.columns.message"}),
            formatMessage({id: "admin.reporting.celebrations.download.columns.receivers"}),
            formatMessage({id: "admin.reporting.celebrations.download.columns.receiversTeams"}),
            formatMessage({id: "admin.reporting.celebrations.download.columns.companyValue"}),
            formatMessage({id: "admin.reporting.celebrations.download.columns.celebration"}),
            formatMessage({id: "admin.reporting.celebrations.download.columns.private"}),
            formatMessage({id: "admin.reporting.celebrations.download.columns.rpGiven"})
          ]

          const recognitionVisibility = (r: Recognition) => {
            if (r.teams?.results?.length) {
              return formatMessage({id: "admin.reporting.celebrations.download.data.team"})
            } else if (r.private) {
              return formatMessage({id: "admin.reporting.celebrations.download.data.private"})
            }
            return formatMessage({id: "admin.reporting.celebrations.download.data.public"})
          }

          const getTeamNames = (teams) => {
            return teams?.results
              .filter(({teamName}) => teamName !== "$$TEAM_ROOT$$")
              .map(({teamName}) => teamName)
              .join(", ")
          }

          const data = recognitionsPerReceiver.map((r: Recognition) => [
            moment(r.createdAt).format("DD/MM/YYYY"),
            firstnameLastname(r.createdBy),
            getTeamNames(r.createdBy.teams),
            r.message,
            firstnameLastname(r.receiver),
            getTeamNames(r.receiver.teams),
            r.companyValues?.results.map(({name}) => name).join(", "),
            formatMessage({id: r.occasion?.name || "N/A"}),
            recognitionVisibility(r),
            r.rpTransferred || 0
          ])
          const csv = Papa.unparse({fields: headers, data})
          makeCSV(csv, "recognitions.csv")
        })
      })

    this.setState({load})
  }

  render() {
    const {load} = this.state
    const {formatMessage, hideText} = this.props

    return (
      <Flex justifyContent={hideText ? "flex-end" : "space-between"} alignItems="center" marginTop={1}>
        {!hideText && (
          <Label>
            <FormattedMessage id="admin.reporting.popularPerks.dataInfo" />
          </Label>
        )}
        <Tooltip tooltipChildren={formatMessage({id: "admin.reporting.celebrations.button.tooltip"})} placement="left">
          <Button id="adminSection_reporting_downloadCelebration_button" primary onClick={this.downloadReport} load={load}>
            <FormattedMessage id="admin.reporting.button.downloadReport" />
          </Button>
        </Tooltip>
      </Flex>
    )
  }
}

export default injectIntl(MostPopularPerks)
